import React from 'react';
import { Helmet } from 'react-helmet';
import { useStaticQuery, graphql } from 'gatsby';

function SEO({
	description = ``,
	lang = 'en',
	meta = [],
	title,
	image,
	hidden,
	schema,
	slug,
	hreflangs,
	canonicalUrl,
}) {
	const { site, wp } = useStaticQuery(
		graphql`
			query {
				site {
					siteMetadata {
						title
						description
						siteUrl
					}
				}
				wp {
					generalSettings {
						title
						description
					}
					globalSettings {
						acfGlobalSettings {
							siteDescription
						}
					}
				}
			}
		`
	);

	const siteTitle = wp.generalSettings.title || site.siteMetadata.title;
	const siteTagline = wp.generalSettings.description;
	const { siteDescription } = wp.globalSettings.acfGlobalSettings;
	const defaultTitle = siteTagline ? `${ siteTitle } | ${ siteTagline } ` : siteTitle;

	const metaTitle = title || siteTitle;
	const metaDescription = description ? description.replace(/(<([^>]+)>)/ig, '') : siteDescription || site.siteMetadata.description;
	const metaImage = image;
	const metaUrl = site.siteMetadata.siteUrl;
	const metaHidden = hidden ? {
		name: `robots`,
		content: 'noindex, nofollow',
	} : {
		name: `robots`,
		content: 'index, follow',
	};

	let canonicalUrlLink = [];
	if (canonicalUrl) {
		canonicalUrlLink = {
			rel: 'canonical',
			href: canonicalUrl,
		};
	}

	let hreflangLinks = [];
	if (hreflangs) {
		hreflangLinks = hreflangs.map(hreflang => ({
			...hreflang,
			rel: 'alternate',
		}));
	} else if (slug) {
		hreflangLinks = [
			{
				rel: 'alternate',
				hreflang: 'en-gb',
				href: `https://balancedbodypilates.co.uk${ slug }`,
			},
			{
				rel: 'alternate',
				hreflang: 'en-us',
				href: `https://www.pilates.com${ slug }`,
			},
			{
				rel: 'alternate',
				hreflang: 'x-default',
				href: `${ metaUrl }${ slug }`,
			},
		];
	}

	return (
		<Helmet
			htmlAttributes={{
				lang,
			}}
			title={title}
			defaultTitle={defaultTitle}
			// titleTemplate={`%s | ${ siteTitle }`}
			titleTemplate={`%s`}
			meta={[
				metaHidden,
				{
					name: `description`,
					content: metaDescription,
				},
				{
					property: `og:url`,
					content: metaUrl,
				},
				{
					property: `og:title`,
					content: metaTitle,
				},
				{
					property: `og:description`,
					content: metaDescription,
				},
				{
					property: `og:image`,
					content: metaImage,
				},
				{
					property: `og:type`,
					content: `website`,
				},
				{
					property: `twitter:card`,
					content: `summary`,
				},
				{
					property: `twitter:title`,
					content: metaTitle,
				},
				{
					property: `twitter:description`,
					content: metaDescription,
				},
				{
					property: `twitter:image`,
					content: metaImage,
				},
			].concat(meta)}
			link={[
				...hreflangLinks,
				...(canonicalUrlLink ? [canonicalUrlLink] : []),
			]}
		>
			{!!schema && (
				<script type={'application/ld+json'}>
					{JSON.stringify(JSON.parse(schema))}
				</script>
			)}

			<script type={'text/javascript'}>
				{`
					window.Userback = window.Userback || {};
					Userback.access_token = 'P-gBl2KmVYZaiSS8SsoWkyzWfwm';
					(function(d) {
							var s = d.createElement('script');s.async = true;
							s.src = 'https://static.userback.io/widget/v1.js';
							(d.head || d.body).appendChild(s);
					})(document);
				`}
			</script>

			<script type={'application/ld+json'}>
				{JSON.stringify({
					'@context': 'https://schema.org',
					'@type': 'Corporation',
					name: 'Balanced Body, Inc.',
					brand: 'Balanced Body',
					url: 'https://www.pilates.com/',
					email: 'mailto:info@pilates.com',
					telephone: [
						'+1(916) 388-2838',
						'+1(800) 745-2837',
					],
					founder: {
						'@type': 'Person',
						name: 'Ken Endelman',
					},
					image: 'https://s3.amazonaws.com/s3.pilates.com/img/main/company/going-green-hero@1x.png',
					logo: 'https://s3.amazonaws.com/s3.pilates.com/img/main/BB_h_2color_rgb.png',
					description: 'Balanced Body is the global leader in Pilates equipment and education for over 40 years.',
					sameAs: [
						'https://video.pilates.com/',
						'https://thecore.pilates.com/',
						'https://checkout.pilates.com/',
						'https://contrology.pilates.com/',
						'https://healthymindhealthybody.pilates.com/',
						'https://diversityinpilates.com/',
						'https://balancedbodyequipment.uk/',
						'http://www.instagram.com/balanced_body/',
						'https://www.facebook.com/BalancedBodyInc',
						'https://www.pinterest.com/balancedbody/',
						'https://www.linkedin.com/company/balanced-body/',
						'https://www.youtube.com/channel/UC9BaEkl0JQt4GGIBXCNSDcg',
						'https://vimeo.com/pilates',
					],
					address: {
						'@type': 'PostalAddress',
						streetAddress: '5909 88th Street',
						addressLocality: 'Sacramento',
						addressRegion: 'CA',
						postalCode: '95828',
						addressCountry: 'US',
					},
					hasMerchantReturnPolicy: {
						returnMethod: 'ReturnByMail',
						merchantReturnDays: '30',
						merchantReturnLink: 'https://www.pilates.com/returns-policy/',
						refundType: [
							'FullRefund',
							'ExchangeRefund',
							'StoreCreditRefund',
						],
						returnFees: 'ReturnFeesCustomerResponsibility',
						returnPolicyCategory: 'MerchantReturnFiniteReturnWindow',
						returnPolicyCountry: {
							'@type': 'Country',
							name: 'US',
						},
					},
					contactPoint: {
						'@type': 'ContactPoint',
						telephone: '+1(800) 745-2837',
						contactType: 'customer service',
					},
				})}
			</script>
		</Helmet>
	);
}

export default SEO;
